import { createTheme } from '@mui/material/styles';

// SmartAI International Africa exact color palette
const c = {
  // Brand gradient stops
  cyan: '#07E3F7',
  cyanLight: '#5EEDF9',
  blue: '#1D9EE1',
  blueAlt: '#4A7FE8',
  indigo: '#3C37C1',
  indigoAlt: '#4F46E5',
  purple: '#4029BD',
  purpleAlt: '#5B4BDB',

  // Dark backgrounds (ink / surface)
  ink950: '#0B0B0B',
  ink900: '#0F1419',
  ink800: '#1A1A2E',
  surface800: '#141B22',
  surface700: '#1A232D',
  surface600: '#242D38',

  // Light backgrounds
  paper50: '#FAFBFD',
  paper100: '#F5F7FA',
  paper200: '#E8EDF4',
  paper300: '#DCE4EF',

  // Text
  darkTextPrimary: '#F5F7FA',
  darkTextSecondary: 'rgba(245, 247, 250, 0.72)',
  lightTextPrimary: '#0B0B0B',
  lightTextSecondary: 'rgba(11, 11, 11, 0.7)',

  // Status
  errorDark: '#EB5757',
  errorLight: '#DC2626',
  warningDark: '#F2C94C',
  warningLight: '#D97706',
  successDark: '#87E7AD',
  successLight: '#059669',

  // Accents
  mint: '#87E7AD',
  teal: '#26CAC0',
};

// Signature brand gradient
const brandGradient = `linear-gradient(90deg, ${c.cyan} 0%, ${c.blue} 35%, ${c.indigo} 70%, ${c.purple} 100%)`;

// Shared typography — Space Grotesk for headings, Inter for body
const typography = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: { fontFamily: '"Space Grotesk", Inter, sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
  h2: { fontFamily: '"Space Grotesk", Inter, sans-serif', fontWeight: 700, letterSpacing: '-0.01em' },
  h3: { fontFamily: '"Space Grotesk", Inter, sans-serif', fontWeight: 600 },
  h4: { fontFamily: '"Space Grotesk", Inter, sans-serif', fontWeight: 600 },
  h5: { fontFamily: '"Space Grotesk", Inter, sans-serif', fontWeight: 600 },
  h6: { fontFamily: '"Space Grotesk", Inter, sans-serif', fontWeight: 600 },
  body1: { lineHeight: 1.6 },
  body2: { lineHeight: 1.6 },
  caption: { fontFamily: '"IBM Plex Mono", monospace', letterSpacing: '0.02em' },
  overline: { fontFamily: '"IBM Plex Mono", monospace', letterSpacing: '0.1em' },
};

// ============================================================================
// Dark Theme
// ============================================================================
export const constellationsThemeDark = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: c.cyan,
      dark: c.blue,
      light: c.cyanLight,
      contrastText: '#0B0B0B',
    },
    secondary: {
      main: c.indigo,
      light: c.indigoAlt,
      dark: c.purple,
      contrastText: '#FFFFFF',
    },
    background: {
      default: c.ink950,
      paper: c.surface800,
    },
    text: {
      primary: c.darkTextPrimary,
      secondary: c.darkTextSecondary,
    },
    divider: 'rgba(255, 255, 255, 0.06)',
    error: { main: c.errorDark },
    warning: { main: c.warningDark },
    info: { main: c.blue },
    success: { main: c.successDark },
  },
  typography,
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `rgba(7,227,247,0.35) ${c.ink900}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 9999,
            backgroundColor: 'rgba(7, 227, 247, 0.35)',
            border: `2px solid ${c.ink900}`,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(7, 227, 247, 0.55)',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: c.ink900,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 999,
          padding: '10px 24px',
          transition: 'all 0.3s ease',
        },
        contained: {
          color: c.ink950,
        },
        containedPrimary: {
          background: brandGradient,
          color: c.ink950,
          boxShadow: 'none',
          '&:hover': {
            background: brandGradient,
            boxShadow: '0 8px 30px rgba(7, 227, 247, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(90deg, ${c.indigo} 0%, ${c.purple} 100%)`,
          boxShadow: 'none',
          '&:hover': {
            background: `linear-gradient(90deg, ${c.indigo} 0%, ${c.purple} 100%)`,
            boxShadow: '0 8px 30px rgba(60, 55, 193, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          '&:hover': {
            borderColor: 'rgba(7, 227, 247, 0.5)',
            backgroundColor: 'rgba(7, 227, 247, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'rgba(20, 27, 34, 0.6)',
          backgroundImage: 'none',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(7, 227, 247, 0.25)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
            },
            '&:hover fieldset': {
              borderColor: 'rgba(7, 227, 247, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: c.cyan,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(15, 20, 25, 0.95)',
          backgroundImage: 'none',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.75rem',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: c.cyan,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: c.surface700,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(12px)',
        },
      },
    },
  },
});

// ============================================================================
// Light Theme
// ============================================================================
export const constellationsThemeLight = createTheme({
  ...constellationsThemeDark,
  palette: {
    mode: 'light',
    primary: {
      main: c.blue,
      dark: c.indigo,
      light: c.blueAlt,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: c.indigo,
      light: c.indigoAlt,
      dark: c.purple,
      contrastText: '#FFFFFF',
    },
    background: {
      default: c.paper50,
      paper: '#FFFFFF',
    },
    text: {
      primary: c.lightTextPrimary,
      secondary: c.lightTextSecondary,
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    error: { main: c.errorLight },
    warning: { main: c.warningLight },
    info: { main: c.blue },
    success: { main: c.successLight },
  },
  components: {
    ...constellationsThemeDark.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${c.paper300} ${c.paper100}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 9999,
            backgroundColor: c.paper300,
            border: `2px solid ${c.paper100}`,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: c.paper100,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          backdropFilter: 'none',
          border: `1px solid ${c.paper200}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(29, 158, 225, 0.35)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        ...constellationsThemeDark.components?.MuiButton?.styleOverrides,
        outlined: {
          borderColor: 'rgba(11, 11, 11, 0.15)',
          backgroundColor: 'rgba(11, 11, 11, 0.02)',
          '&:hover': {
            borderColor: 'rgba(29, 158, 225, 0.5)',
            backgroundColor: 'rgba(29, 158, 225, 0.06)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: c.lightTextPrimary,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${c.paper200}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#FFFFFF',
          color: c.lightTextPrimary,
          border: `1px solid ${c.paper200}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});
